import { z } from 'zod';
import { baseEntityFields, idSchema, isoDateTimeSchema, propertyExpressionStringSchema } from './common.schema';
/**
 * Migration scopes (#15) — the unit a migration moves through its lifecycle.
 * Lifecycle and rules: docs/architecture/migration.md.
 *
 * A scope is a client, site or device group (a selector over Detect resources)
 * that is being moved from an incumbent platform. It carries ONE stage; the
 * stage decides whether Detect holds delivery for the resources it covers
 * ("silent mode"): suppression mirrors SDT semantics — the alert exists with
 * full truth, only routing is held.
 */
/** Hard cap per tenant: the evaluator reads a tenant's scopes on its hot path,
 *  so the set must stay small and bounded. */
export const MAX_MIGRATION_SCOPES_PER_TENANT = 50;
export const MIGRATION_VENDORS = ['logicmonitor'];
/** connect → import → tune → silent → live. */
export const MIGRATION_STAGES = ['connect', 'import', 'tune', 'silent', 'live'];
/** Stages in which Detect monitors fully but HOLDS delivery. `connect` has no
 *  migrated monitoring yet; `live` is cut over (Detect pages). */
export const SILENT_STAGES = ['import', 'tune', 'silent'];
export const isSilentStage = (s) => SILENT_STAGES.includes(s);
const MAX_LIST = 200;
/** Which Detect resources a scope covers. Exactly one mode: the whole tenant
 *  (explicit `all`), or the union-of-filters that alert rules also use. */
export const migrationSelectorSchema = z
    .object({
    all: z.literal(true).optional(),
    clientIds: z.array(idSchema).min(1).max(MAX_LIST).optional(),
    resourceGroups: z.array(z.string().min(1).max(200)).min(1).max(MAX_LIST).optional(),
    resourceIds: z.array(idSchema).min(1).max(MAX_LIST).optional(),
    propertyExpression: propertyExpressionStringSchema.optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    const filters = [val.clientIds, val.resourceGroups, val.resourceIds, val.propertyExpression].filter((f) => f !== undefined).length;
    if (val.all === true && filters > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '"all" cannot be combined with filters' });
    }
    if (val.all !== true && filters === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'a scope needs a selector: name clients/groups/resources/an expression, or set all: true explicitly',
        });
    }
});
export const migrationScopeInputSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    vendor: z.enum(MIGRATION_VENDORS).default('logicmonitor'),
    selector: migrationSelectorSchema,
})
    .strict();
/** Rename and/or re-target. The stage is changed ONLY through the stage endpoint. */
export const migrationScopeUpdateSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    selector: migrationSelectorSchema.optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.selector !== undefined, {
    message: 'provide name and/or selector',
});
export const migrationScopeSchema = z
    .object({
    name: z.string().min(1).max(200),
    vendor: z.enum(MIGRATION_VENDORS),
    selector: migrationSelectorSchema,
    stage: z.enum(MIGRATION_STAGES),
    stageChangedAt: isoDateTimeSchema,
    stageChangedBy: idSchema,
})
    .extend(baseEntityFields)
    .strict();
export const migrationStageChangeSchema = z
    .object({ to: z.enum(MIGRATION_STAGES) })
    .strict();
/**
 * Allowed stage moves. Forward is ONE step at a time (no skipping Tune/Silent
 * run); backward is always allowed to any earlier stage, except that a LIVE
 * scope can only be reversed one step — to `silent` — because that reverse is
 * the cutover undone (docs/architecture/migration.md). `silent → live` is the
 * cutover itself; whether it may run is decided by the caller (it needs the
 * incumbent connection).
 */
export function checkStageMove(from, to) {
    const a = MIGRATION_STAGES.indexOf(from);
    const b = MIGRATION_STAGES.indexOf(to);
    if (a === b)
        return { ok: false, reason: `The scope is already at "${from}".` };
    if (b === a + 1)
        return { ok: true, kind: 'forward' };
    if (b > a) {
        return { ok: false, reason: `Stages go one step at a time: "${from}" → "${MIGRATION_STAGES[a + 1]}".` };
    }
    if (from === 'live' && to !== 'silent') {
        return { ok: false, reason: 'A live scope is reversed one step, to "silent" (undoing the cutover).' };
    }
    return { ok: true, kind: 'backward' };
}
/* ------------------------------------------------------------------ */
/* Incumbent connection (#15 phase 2)                                  */
/* ------------------------------------------------------------------ */
/** The LM portal slug (`ttg` for ttg.logicmonitor.com): a single DNS label.
 *  This is a TENANT-SUPPLIED value that becomes part of a request host, so the
 *  pattern is deliberately narrow (no dots, slashes, ports, userinfo,
 *  unicode); the host suffix is fixed in code. */
export const lmCompanySchema = z
    .string()
    .regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/, 'a LogicMonitor portal name is lowercase letters, digits and hyphens (e.g. "ttg")');
/** A credential VALUE as accepted by the API: write-only, trimmed, never echoed. */
const connectionSecret = z.string().trim().min(1).max(512);
/** CONNECT / rotate: the credential is entered once and stored by the server
 *  under server-derived names (tenant-secrets.md); the API never returns it. */
export const migrationConnectionInputSchema = z
    .object({
    vendor: z.enum(MIGRATION_VENDORS).default('logicmonitor'),
    company: lmCompanySchema,
    accessId: connectionSecret,
    accessKey: connectionSecret,
})
    .strict();
export const MIGRATION_CONNECTION_STATUSES = ['verified', 'failed'];
/** What the incumbent holds, as counted by the verification (null = not
 *  readable with this credential, or LM's "too many to count"). */
export const INCUMBENT_INVENTORY_KEYS = [
    'devices',
    'deviceGroups',
    'websites',
    'dataSources',
    'eventSources',
    'propertySources',
    'collectors',
    'alertRules',
    'escalationChains',
    'recipientGroups',
    'sdts',
];
export const incumbentInventorySchema = z
    .object(Object.fromEntries(INCUMBENT_INVENTORY_KEYS.map((k) => [k, z.number().int().nonnegative().nullable()])))
    .strict();
export const migrationConnectionSchema = z
    .object({
    vendor: z.enum(MIGRATION_VENDORS),
    company: lmCompanySchema,
    /** SYSTEM-GENERATED Secret Manager name (never client-writable) of the ONE
     *  secret holding the Access ID and Key together, so a rotation is a single
     *  atomic write (a new ID can never pair with an old Key). */
    credentialSecretName: z.string().min(1).max(255),
    status: z.enum(MIGRATION_CONNECTION_STATUSES),
    lastVerifiedAt: isoDateTimeSchema.nullable(),
    /** Why the last verification failed — a classified, credential-free message. */
    lastError: z.string().max(500).nullable(),
    /** `read` is proven by the verification. Whether the credential may WRITE
     *  (needed only by the cutover) is not provable without writing, so it is
     *  `unverified` until the cutover preflight (`proven` / `denied`), which refuses to start
     *  without it (docs/architecture/migration.md). */
    capability: z.object({ read: z.boolean(), write: z.enum(['unverified', 'proven', 'denied']) }).strict(),
    /** SYSTEM-GENERATED name of the secret holding the OPTIONAL write-capable
     *  credential (cutover only; docs/architecture/migration.md). Null until entered. */
    writeCredentialSecretName: z.string().min(1).max(255).nullable().default(null),
    /** Identifies the exact write credential entered (also inside its secret), so what the
     *  cutover preflight proved can only ever be recorded against THAT credential. */
    writeCredentialRevision: z.string().min(8).max(64).nullable().default(null),
    inventory: incumbentInventorySchema.nullable(),
})
    .extend(baseEntityFields)
    .strict();
/** The write-capable credential (cutover only): the portal is the connection's. */
export const migrationWriteCredentialInputSchema = z
    .object({
    accessId: connectionSecret,
    accessKey: connectionSecret,
})
    .strict();
/* ------------------------------------------------------------------ */
/* Import (#15 phase 3)                                                */
/* ------------------------------------------------------------------ */
/** Which LM devices an import run covers. Exactly one mode (explicit, never
 *  "everything by omission"). */
export const migrationDeviceSelectionSchema = z
    .object({
    allDevices: z.literal(true).optional(),
    /** LM device-group ids (a device is in scope if it is a member of any). */
    deviceGroupIds: z.array(z.number().int().nonnegative()).min(1).max(50).optional(),
    deviceIds: z.array(z.number().int().nonnegative()).min(1).max(200).optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    const modes = [val.allDevices === true, val.deviceGroupIds !== undefined, val.deviceIds !== undefined].filter(Boolean).length;
    if (modes !== 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'choose exactly one of allDevices, deviceGroupIds, deviceIds' });
    }
});
/** Import request. `dryRun` is REQUIRED (no default): a run is either a report or
 *  it creates entities, and the caller must say which. */
export const migrationImportRequestSchema = z
    .object({
    dryRun: z.boolean(),
    devices: migrationDeviceSelectionSchema.optional(),
    /** Continue a capped run: only devices with an LM id GREATER than this are
     *  considered (the previous report's `nextAfterDeviceId`). */
    afterDeviceId: z.number().int().nonnegative().optional(),
    includeWebsites: z.boolean().default(false),
    /** Continue a capped website run: only websites with an LM id GREATER than this. */
    afterWebsiteId: z.number().int().nonnegative().optional(),
    /** LM collector id (digits) → Detect collector id. Devices behind an unmapped
     *  LM collector WAIT — they are reported, not dropped. */
    collectorMap: z.record(z.string().regex(/^\d{1,12}$/), idSchema).default({}),
    clientId: idSchema.optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.devices === undefined && !val.includeWebsites) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'select devices and/or set includeWebsites' });
    }
});
/** Tuning request (#15 stage "tune"): how much incumbent alert history to mine.
 *  Read-only on both sides — it proposes, a human applies. */
export const MIGRATION_TUNE_LIMITS = { minDays: 1, maxDays: 90, defaultDays: 30 };
export const migrationTuneRequestSchema = z
    .object({
    sinceDays: z
        .number()
        .int()
        .min(MIGRATION_TUNE_LIMITS.minDays)
        .max(MIGRATION_TUNE_LIMITS.maxDays)
        .default(MIGRATION_TUNE_LIMITS.defaultDays),
})
    .strict();
/** Comparison request (#15 stage "silent"): the window to compare Detect with the incumbent over. */
export const migrationCompareRequestSchema = migrationTuneRequestSchema;
/* ------------------------------------------------------------------ */
/* Cutover / reverse (#15 phase 6)                                     */
/* ------------------------------------------------------------------ */
export const CUTOVER_LIMITS = { maxDevices: 5000 };
/** `silent → live` and its reverse, as human-confirmed actions: the caller types the scope's name. */
export const migrationCutoverRequestSchema = z.object({ confirm: z.string().min(1).max(200) }).strict();
export const CUTOVER_PHASES = [
    /** Reading each device's current alerting state; nothing has changed anywhere. */
    'planned',
    /** Detect is live; the incumbent still alerts for some devices (loud, retryable). */
    'detect-live-incumbent-alerting',
    /** Every device is paged by Detect alone. */
    'complete',
    /** Re-enabling the incumbent's alerting; Detect stays live until all are confirmed. */
    'reversing',
    /** Back to silent, the incumbent alerting again. */
    'reversed',
];
export const CUTOVER_DEVICE_STATES = [
    'unplanned', // prior state not read yet
    'pending', // prior state known, nothing written
    'switched', // WE disabled the incumbent's alerting for it (confirmed by read-back)
    'already-disabled', // it was disabled before we came; never ours to re-enable
    'skipped', // not found in the incumbent: nothing there can page
    'failed', // the last attempt failed (see error)
    'restored', // reverse: alerting re-enabled (confirmed)
];
export const cutoverDeviceSchema = z
    .object({
    lmDeviceId: z.number().int().positive(),
    /** The incumbent's `disableAlerting` BEFORE we touched it (null until read). */
    priorDisabled: z.boolean().nullable(),
    state: z.enum(CUTOVER_DEVICE_STATES),
    error: z.string().max(200).nullable(),
})
    .strict();
export const migrationCutoverSchema = z
    .object({
    scopeId: idSchema,
    phase: z.enum(CUTOVER_PHASES),
    devices: z.array(cutoverDeviceSchema).max(CUTOVER_LIMITS.maxDevices),
    /** The scope's `updatedAt` when its devices were listed: the stage flip refuses if the scope
     *  (e.g. its selector) changed since, so a cutover never goes live over a stale device list. */
    scopeRevision: isoDateTimeSchema.nullable().default(null),
    /** Which write credential revision the preflight used (audit only). */
    credentialRevision: z.string().max(64).nullable(),
    startedAt: isoDateTimeSchema,
    startedBy: idSchema,
    /** One run at a time per scope. */
    lease: z.object({ owner: z.string().min(1).max(64), until: isoDateTimeSchema }).strict().nullable(),
})
    .extend(baseEntityFields)
    .strict();
//# sourceMappingURL=migration.schema.js.map